import { Box } from "@mui/material";
import { ROUTES_SURVEY } from "commons/constants/paths";
import { useAppDispatch } from "commons/store/hooks";
import ColoredBox from "components/ColoredBox";
import Table from "components/Table";
import {
    fetchSurveySegments,
    resetSurveySegments,
} from "features/dashboard/redux/dashboardSlice";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectUserProfile } from "core/redux/userSlice";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CallMadeIcon from '@mui/icons-material/CallMade';
import { roles } from 'commons/auth/roles';
import { FilterPanel } from "./FilterPanel";
import SegmentList from "./SegmentList";
import { enqueueSnackbar } from 'notistack'
import FormHelper from "core/helpers/Form";
import { useTranslation } from "react-i18next";
import { useMercure } from "utils/mercure";
import { useQueryForms } from "utils/form";
import { LoadingButton } from "@mui/lab";
import api from "utils/__api__/surveys";
import { useMutation } from "@tanstack/react-query";
import { Form } from "types/form/Form";

const DashboardSurveys = () => {
  // const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState('1');
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [searchParams] = useSearchParams();
  // const [open, setOpen] = useState(false);
  // const handleOpen = () => setOpen(true);

  const { isLoading, data: { data: data, hubURL: hubURL } = { hubURL: null } } = useQueryForms(null, page);
  const collection = useMercure(data, hubURL);
  const items = collection?.["member"] || [];
  const totalItems = (data && data["totalItems"]) || 0;

  const saveMutation = useMutation({
    mutationFn: async (values: any) => api.save(values)
  });

  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});
  const handleCreateSurvey = async (row: Form) => {
    if (!row.id) return;

    setLoadingRows(prev => ({ ...prev, [row.id]: true }));

    const surveyId = row?.surveys?.[0]?.id;
    surveyId ? navigate(ROUTES_SURVEY.DETAILS(surveyId))
      : saveMutation.mutate(
        row?.['@id'] ? { form: row['@id'] } : {},
        {
          onSuccess: (result) => {
            (result?.data?.['@id'] && result?.data?.id) && navigate(ROUTES_SURVEY.DETAILS(result.data.id));
          },
          onError: (error: any) => {
            enqueueSnackbar(`${"status" in error ? error.status : error.message}`, { variant: "error" });
          },
          onSettled: () => {
            setLoadingRows(prev => ({ ...prev, [row.id]: false }));
          },
        },
    );
  };

  // const handleClose = () => {
  //   dispatch(resetSurveySegments())
  //   setOpen(false)
  // };

  // /**
  //  * This function is used to fetch data of surveys segments
  //  */
  // const [isSegmentsLoading, setSegmentsLoading] = useState(false)
  // const fetchSegments = async (row: Form) => {
  //   setLoadingRows(prev => ({ ...prev, [row.id]: true }));
  //   setSegmentsLoading(true);
  //
  //   handleOpen();
  //   row?.surveys?.[0]?.id && await dispatch(fetchSurveySegments(row.surveys?.[0].id))
  //
  //   setSegmentsLoading(false);
  //   setLoadingRows(prev => ({ ...prev, [row.id]: false }));
  // }

  /**
   * Check user role and hide export button for
   * All users except: admin, user, etc.
   */
  // const userProfile = useAppSelector(selectUserProfile);
  // const displaySegmentList: boolean = userProfile.roles.some(
  //     (role: string) => roles.admin.includes(role) && !roles.control.includes(role)
  // );

  useEffect(() => {
    // fetchData().then()
  }, [searchParams]);

  const columns = () => {
    const columns: any[] = [
      {
        title: <Box ml="20px">{t('Id')}</Box>,
        render: (row: any) => (
          <Box
            style={{
              opacity: FormHelper.statusDisabled(row?.status) ? '50%' : '100%',
              textAlign: "left",
            }}>
            <LoadingButton
              variant="contained"
              color="primary"
              style={{ paddingLeft: "30px", paddingRight: "30px" }}
              onClick={() => handleCreateSurvey(row)}
              loading={!!loadingRows[row.id]}
              disabled={FormHelper.statusDisabled(row?.status)}
            >
              <span title={row.id}>#{row.id.split("-")[0]}</span>
            </LoadingButton>
          </Box>
        ),
        key: 'id',
      },
      {
        title: <Box ml="20px">{t('name')}</Box>,
        render: (row: any) => (
          <Box
            style={{
              opacity: FormHelper.statusDisabled(row?.status) ? '50%' : '100%',
              textAlign: "left",
            }}>
            {row?.name}
          </Box>
        ),
        key: 'name',
      },
      {
        title: <Box ml="20px">{t('status')}</Box>,
        render: (row: any) => (
          <Box style={{ opacity: FormHelper.statusDisabled(row?.status) ? '50%' : '100%' }}>
            {
              row?.surveys?.[0]?.statusName ? t(row.surveys?.[0].statusName) : t(row?.statusName)
            }
          </Box>
        ),
        key: 'status',
      },
    ]

    // columns.push({
    //     title: <Box textAlign={'center'} minWidth="60px">{t('comments')}</Box>,
    //     render: (row: Form) => (
    //         <Box style={{opacity: SurveyStatus.statusClosed(row?.status) ? '50%' : '100%', placeItems: "center"}}
    //              textAlign={'center'}
    //              display="grid">
    //             <Box
    //                 fontWeight={row?.unreadComments > 0 ? 700 : 400}
    //                 display="flex"
    //                 textAlign="center"
    //             >
    //                 {row?.unreadComments}
    //             </Box>
    //         </Box>
    //     ),
    //     key: 'comments',
    // })

    columns.push({
      title: <Box ml="20px">{t('form')}</Box>,
      noWrap: true,
      render: (row: Form) => (
        <Box style={{ opacity: FormHelper.statusDisabled(row?.status) ? '50%' : '100%' }}>
          <Box display="inline-flex">
            <LoadingButton
              variant="outlined"
              color="primary"
              size="small"
              endIcon={<CallMadeIcon/>}
              onClick={() => handleCreateSurvey(row)}
              loading={!!loadingRows[row.id]}
              disabled={FormHelper.statusDisabled(row?.status)}
            >
              {t('Fill out')}
            </LoadingButton>
          </Box>
        </Box>
      ),
      key: 'actions',
    })

    return columns
  }

  return (
    <ColoredBox pt={0} px={5} maxWidth="100%" ml="auto" mr="auto">
      {/*<FilterPanel />*/}
      <Table
        pagination={{
          current: Number(page),
          pageSize: itemsPerPage,
          total: totalItems,
        }}
        onChangePage={(page) => setPage(String(page))}
        data={items}
        tableName={t('Surveys')}
        columns={columns()}
        isLoading={isLoading}
      />

      {/*<SegmentList loading={isSegmentsLoading} open={open} handleClose={handleClose} />*/}
    </ColoredBox>
  );
};

export default DashboardSurveys;